import { useEffect, useMemo, useState } from "react";
import { Bookmark, Columns3, Download, FileText, KanbanSquare, Rows3, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { EmptyState } from "@/components/common";

const plain = (c, r) => (c.value ? c.value(r) : r[c.key]);
const show = (c, r) => (c.render ? c.render(r) : (plain(c, r) ?? "—"));

async function download(title, format, cols, rows) {
  try {
    const body = { title, format, columns: cols.map((c) => ({ key: c.key, label: c.label })), rows: rows.map((r) => Object.fromEntries(cols.map((c) => [c.key, plain(c, r)]))) };
    const { data, headers } = await api.post("/admin/export", body, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || `monrezo.${format}`;
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function SavedViews({ page, state, apply }) {
  const [views, setViews] = useState([]);
  const load = () => api.get("/admin/views", { params: { page } }).then(({ data }) => setViews(data)).catch(() => {});
  useEffect(() => {
    load();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const name = window.prompt("Nom de la vue (visible par vous seul)");
    if (!name?.trim()) return;
    await api.post("/admin/views", { page, name, state }).then(() => { toast.success("Vue enregistrée"); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`${page}-views-button`} className="mr-btn-secondary px-3 min-h-[40px]"><Bookmark size={15} /> Vues</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Mes vues enregistrées</DropdownMenuLabel>
        {views.length === 0 && <p className="px-2 py-1 text-xs text-mr-muted">Aucune vue pour le moment.</p>}
        {views.map((v) => (
          <DropdownMenuItem key={v.id} data-testid={`${page}-view-item`} onClick={() => apply(v.state)} className="justify-between">
            <span className="truncate">{v.name}</span>
            <button aria-label="Supprimer la vue" data-testid={`${page}-view-delete`} onClick={(e) => { e.stopPropagation(); api.delete(`/admin/views/${v.id}`).then(load); }}><Trash2 size={13} className="text-mr-muted" /></button>
          </DropdownMenuItem>))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`${page}-view-save`} onClick={save}>Enregistrer la vue actuelle…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ColumnChooser({ page, columns, hidden, setHidden }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`${page}-columns-button`} className="mr-btn-secondary px-3 min-h-[40px]"><Columns3 size={15} /> Colonnes</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {columns.map((c) => (
          <DropdownMenuCheckboxItem key={c.key} data-testid={`${page}-column-${c.key}`} checked={!hidden.includes(c.key)} onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) => setHidden(on ? hidden.filter((k) => k !== c.key) : [...hidden, c.key])}>{c.label}</DropdownMenuCheckboxItem>))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Kanban({ page, rows, kanban, cols, open }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid={`${page}-kanban`}>
      {kanban.columns.map(([v, l]) => {
        const items = rows.filter((r) => kanban.get(r) === v);
        return (
          <div key={v} className="w-64 shrink-0 bg-mr-surface border border-mr-border rounded-[3px] p-2" data-testid={`${page}-kanban-col-${v}`}>
            <p className="text-xs uppercase tracking-wider text-mr-muted px-1 pb-2 flex justify-between"><span>{l}</span><span className="tabular">{items.length}</span></p>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">{items.map((r) => (
              <button key={r.id} data-testid={`${page}-kanban-card`} onClick={() => open(r)} className="w-full text-left mr-card p-3 hover:border-mr-accent transition-colors">
                <p className="text-sm font-medium">{show(cols[0], r)}</p>
                <p className="text-xs text-mr-muted mt-1">{cols.slice(1, 3).map((c) => plain(c, r)).filter(Boolean).join(" · ")}</p>
              </button>))}</div>
          </div>);
      })}
    </div>
  );
}

function Detail({ row, columns, title, onClose, extra }) {
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-mr-bg border-mr-border w-full sm:max-w-md overflow-y-auto" data-testid="record-detail">
        <SheetTitle className="font-display">{title}</SheetTitle>
        {row && <dl className="mt-6 divide-y divide-mr-border">{columns.map((c) => (
          <div key={c.key} className="py-2.5 grid grid-cols-[130px_1fr] gap-3 text-sm"><dt className="text-mr-muted">{c.label}</dt><dd className="break-words">{show(c, row)}</dd></div>))}</dl>}
        {row && extra && <div className="mt-6">{extra(row)}</div>}
      </SheetContent>
    </Sheet>
  );
}

export function DataView({ page, title, rows, columns, filters = [], kpis = [], kanban, bulk = [], onRowClick, detailExtra, empty = "Aucun élément", searchKeys }) {
  const colKey = `mr-cols-${page}`;
  const [q, setQ] = useState("");
  const [f, setF] = useState({});
  const [hidden, setHidden] = useState(() => JSON.parse(localStorage.getItem(colKey) || "null") || columns.filter((c) => c.hidden).map((c) => c.key));
  const [mode, setMode] = useState("table");
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    localStorage.setItem(colKey, JSON.stringify(hidden));
  }, [colKey, hidden]);
  const shown = rows.filter((r) => {
    if (q && !(searchKeys || columns.map((c) => c.key)).some((k) => String((columns.find((c) => c.key === k) ? plain(columns.find((c) => c.key === k), r) : r[k]) ?? "").toLowerCase().includes(q.toLowerCase()))) return false;
    return filters.every((fl) => !f[fl.key] || String(fl.get ? fl.get(r) : r[fl.key]) === f[fl.key]);
  });
  const visible = columns.filter((c) => !hidden.includes(c.key));
  const ids = useMemo(() => new Set(shown.map((r) => r.id)), [shown]);
  const picked = shown.filter((r) => sel.includes(r.id));
  const all = shown.length > 0 && shown.every((r) => sel.includes(r.id));
  const openRow = (r) => (onRowClick ? onRowClick(r) : setOpen(r));
  const exportRows = picked.length ? picked : shown;
  const chips = filters.filter((fl) => f[fl.key]).map((fl) => [fl.key, `${fl.label} : ${(fl.options.find(([v]) => v === f[fl.key]) || [])[1] || f[fl.key]}`]);
  const apply = (s) => { setQ(s.q || ""); setF(s.f || {}); if (s.hidden) setHidden(s.hidden); setMode(s.mode || "table"); };
  return (
    <div className="space-y-4" data-testid={`${page}-dataview`}>
      {kpis.length > 0 && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`${page}-kpis`}>{kpis.map((k) => (
        <div key={k.key} className="mr-card p-4" data-testid={`${page}-kpi-${k.key}`}><p className="text-xs uppercase tracking-wider text-mr-muted">{k.label}</p><p className="font-display font-bold text-2xl tabular mt-1">{k.value(rows)}</p></div>))}</div>}
      <div className="flex flex-wrap gap-2 items-center">
        <label className="relative flex-1 min-w-[200px] max-w-sm"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-mr-muted" />
          <input data-testid={`${page}-search`} className="mr-input pl-9" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        {filters.map((fl) => (
          <select key={fl.key} data-testid={`${page}-filter-${fl.key}`} className="mr-input w-auto min-w-[150px]" value={f[fl.key] || ""} onChange={(e) => setF({ ...f, [fl.key]: e.target.value })}>
            <option value="">{fl.label} : tous</option>{fl.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>))}
        <div className="flex gap-2 ml-auto flex-wrap">
          {kanban && <button data-testid={`${page}-mode-toggle`} className="mr-btn-secondary px-3 min-h-[40px]" onClick={() => setMode(mode === "table" ? "kanban" : "table")}>{mode === "table" ? <><KanbanSquare size={15} /> Colonnes par étape</> : <><Rows3 size={15} /> Tableau</>}</button>}
          {mode === "table" && <ColumnChooser page={page} columns={columns} hidden={hidden} setHidden={setHidden} />}
          <SavedViews page={page} state={{ q, f, hidden, mode }} apply={apply} />
          <button data-testid={`${page}-export-xlsx`} className="mr-btn-secondary px-3 min-h-[40px]" disabled={!exportRows.length} onClick={() => download(title, "xlsx", visible, exportRows)}><Download size={15} /> Excel</button>
          <button data-testid={`${page}-export-pdf`} className="mr-btn-secondary px-3 min-h-[40px]" disabled={!exportRows.length} onClick={() => download(title, "pdf", visible, exportRows)}><FileText size={15} /> PDF</button>
        </div>
      </div>
      {chips.length > 0 && <div className="flex flex-wrap gap-2" data-testid={`${page}-chips`}>{chips.map(([k, l]) => (
        <button key={k} data-testid={`${page}-chip-${k}`} onClick={() => setF({ ...f, [k]: "" })} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-mr-accent text-mr-accent text-xs">{l} <X size={12} /></button>))}
        <button data-testid={`${page}-chips-clear`} className="text-xs text-mr-muted underline" onClick={() => setF({})}>Tout effacer</button></div>}
      {picked.length > 0 && (
        <div className="sticky top-16 z-20 flex flex-wrap items-center gap-2 p-3 bg-mr-raised border border-mr-accent rounded-[3px]" data-testid={`${page}-bulk-bar`}>
          <span className="text-sm font-medium tabular" data-testid={`${page}-bulk-count`}>{picked.length} sélectionné(s)</span>
          {bulk.map((b) => <button key={b.key} data-testid={`${page}-bulk-${b.key}`} className="mr-btn-secondary min-h-[36px] px-3" onClick={() => b.run(picked, () => setSel([]))}>{b.label}</button>)}
          <button data-testid={`${page}-bulk-clear`} className="mr-btn-ghost min-h-[36px] px-3 ml-auto" onClick={() => setSel([])}>Désélectionner</button>
        </div>)}
      <p className="text-xs text-mr-muted tabular" data-testid={`${page}-count`}>{shown.length} sur {rows.length}{picked.length ? ` · export limité à la sélection` : ""}</p>
      {shown.length === 0 ? <EmptyState title={empty} testId={`${page}-empty`} /> : mode === "kanban" && kanban ? <Kanban page={page} rows={shown} kanban={kanban} cols={columns} open={openRow} /> : (
        <div className="overflow-x-auto border border-mr-border"><table className="mr-table w-full">
          <thead><tr><th className="w-10"><input type="checkbox" aria-label="Tout sélectionner" data-testid={`${page}-select-all`} checked={all} onChange={() => setSel(all ? sel.filter((i) => !ids.has(i)) : [...new Set([...sel, ...shown.map((r) => r.id)])])} /></th>
            {visible.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>{shown.map((r) => (
            <tr key={r.id} data-testid={`${page}-row`} className="cursor-pointer hover:bg-mr-surface" onClick={() => openRow(r)}>
              <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Sélectionner" data-testid={`${page}-select`} checked={sel.includes(r.id)} onChange={() => setSel(sel.includes(r.id) ? sel.filter((i) => i !== r.id) : [...sel, r.id])} /></td>
              {visible.map((c) => <td key={c.key} className={c.className || ""}>{show(c, r)}</td>)}
            </tr>))}</tbody>
        </table></div>)}
      <Detail row={open} columns={columns} title={open ? String(plain(columns[0], open)) : ""} onClose={() => setOpen(null)} extra={detailExtra} />
    </div>
  );
}
